import React, { useState } from "react";

const faqItems = [
  {
    question: "I can't log in / I see \"Access Denied\"",
    answer: "Your Microsoft account may not be registered in the portal. Please submit a support ticket in the support portal requesting access, and an administrator will get your account set up."
  },
  {
    question: "I searched for an employee but got \"User not found\"",
    answer: "Double-check the Employee ID or email address. The search is exact \u2014 partial matches are not supported. Make sure there are no extra spaces."
  },
  {
    question: "The DOR Email or Manager dropdown says \"Loading...\" or shows an error",
    answer: "This means the portal could not reach Microsoft Entra ID to fetch the list. This is usually a temporary connectivity or permissions issue. Please submit a support ticket in the support portal so IT can investigate."
  },
  {
    question: "I changed an employee's type from FT but their licenses weren't removed",
    answer: "Verify the change was saved successfully (look for the green success message). If the change saved but licenses were not removed, please submit a support ticket in the support portal \u2014 the application may need additional Azure permissions."
  },
  {
    question: "The Facility Email didn't auto-populate when I selected a facility",
    answer: "This happens when no user in Entra ID has a Custom Attribute 05 matching that facility's code. You will need to fill in the Facility Email manually. If you believe a match should exist, submit a support ticket in the support portal to have IT verify the Exchange Custom Attributes for that facility."
  },
  {
    question: "Notification emails aren't being sent",
    answer: "Notification recipient lists and email templates are managed by administrators. If you believe notifications should be going out but aren't, please submit a support ticket in the support portal describing the issue."
  },
  {
    question: "I accidentally disabled someone's account",
    answer: "Search for the user in the Status Changes tab, click Edit, change the Account Status back to \"Enabled\", and save. The account will be re-enabled immediately."
  },
  {
    question: "A record is locked by someone else",
    answer: "Records in the Process Employees tab are automatically locked when someone selects them. If the person is no longer working on it, please submit a support ticket in the support portal so an administrator can release the lock."
  },
  {
    question: "Additional notification recipients aren't receiving emails",
    answer: "Make sure you either press Enter or click \"Add\" after typing the email addresses before saving. You can also type them directly and click Save \u2014 the system will auto-detect valid emails in the input field. Ensure the emails contain an @ symbol and are correctly formatted."
  },
  {
    question: "My submission to Power Automate failed",
    answer: "Check that all required fields are filled in and valid (highlighted in red if not). If the submission still fails, please submit a support ticket in the support portal with the employee name and error message so an administrator can investigate and retry it."
  },
  {
    question: "How do I add a new employee manually?",
    answer: "Click the \"Add New Employee\" button above the employee selector dropdown. This creates a blank form you can fill out. Once complete, click Save and then Submit to Power Automate."
  },
  {
    question: "I need a dropdown option added (Job Title, Facility, etc.)",
    answer: "Dropdown lists are managed by administrators. Please submit a support ticket in the support portal with the list name and the value you need added, and an administrator will update it for you."
  },
  {
    question: "How do I get administrator access?",
    answer: "Administrator roles are assigned by existing portal administrators. Please submit a support ticket in the support portal requesting admin access, including your name and email address."
  }
];

const HelpModal = ({ isOpen, onClose }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  if (!isOpen) return null;

  const filteredFaqs = searchQuery.trim()
    ? faqItems.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqItems;

  return (
    <div
      data-testid="help-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(4px)",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        paddingTop: "3vh", overflowY: "auto"
      }}
    >
      <div
        data-testid="help-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: "12px",
          width: "min(680px, 94vw)", maxHeight: "90vh",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column",
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <div style={{
          padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e40af 0%, #2563eb 100%)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span style={{ color: "white", fontSize: "1.15rem", fontWeight: 700 }}>Help Center</span>
          </div>
          <button
            data-testid="help-modal-close"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer",
              color: "white", width: "32px", height: "32px", borderRadius: "8px",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.1rem", fontWeight: 700, transition: "background 0.15s"
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.35)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
          >
            X
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Quick Links */}
          <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr", gap: "0.75rem"
            }}>
              <a
                href="/training-manual.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="help-full-manual-link"
                style={{
                  display: "flex", alignItems: "center", gap: "0.6rem",
                  padding: "0.85rem 1rem", borderRadius: "8px",
                  border: "1px solid #bfdbfe", background: "white",
                  textDecoration: "none", color: "#1e40af", fontWeight: 600,
                  fontSize: "0.9rem", transition: "all 0.15s",
                  gridColumn: "1 / -1"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#eff6ff";
                  e.currentTarget.style.borderColor = "#93c5fd";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#bfdbfe";
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                </svg>
                Full Training Manual
              </a>
            </div>
          </div>

          {/* FAQ Section */}
          <div style={{ padding: "1.25rem 1.5rem" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b", marginBottom: "0.75rem" }}>
              Frequently Asked Questions
            </h3>

            {/* Search */}
            <div style={{ position: "relative", marginBottom: "1rem" }}>
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)" }}
              >
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                data-testid="help-faq-search"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%", padding: "0.6rem 0.75rem 0.6rem 2.25rem",
                  border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "0.88rem",
                  outline: "none", transition: "border-color 0.15s",
                  background: "#f8fafc"
                }}
                onFocus={(e) => (e.target.style.borderColor = "#93c5fd")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>

            {/* FAQ Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {filteredFaqs.length === 0 && (
                <p style={{ color: "#94a3b8", fontSize: "0.88rem", padding: "1rem 0", textAlign: "center" }}>
                  No matching questions found. Try a different search term or check the full training manual.
                </p>
              )}
              {filteredFaqs.map((item, idx) => {
                const isExpanded = expandedFaq === idx;
                return (
                  <div
                    key={idx}
                    data-testid={`help-faq-item-${idx}`}
                    style={{
                      border: "1px solid " + (isExpanded ? "#bfdbfe" : "#e2e8f0"),
                      borderRadius: "8px", overflow: "hidden",
                      background: isExpanded ? "#f0f7ff" : "white",
                      transition: "all 0.15s"
                    }}
                  >
                    <button
                      data-testid={`help-faq-toggle-${idx}`}
                      onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                      style={{
                        width: "100%", textAlign: "left", cursor: "pointer",
                        padding: "0.75rem 1rem", border: "none",
                        background: "transparent", display: "flex",
                        alignItems: "center", justifyContent: "space-between",
                        gap: "0.75rem", fontSize: "0.88rem", fontWeight: 600,
                        color: isExpanded ? "#1e40af" : "#334155",
                        lineHeight: 1.4
                      }}
                    >
                      <span>{item.question}</span>
                      <svg
                        width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{
                          flexShrink: 0,
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s"
                        }}
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                    {isExpanded && (
                      <div style={{
                        padding: "0 1rem 0.85rem",
                        fontSize: "0.85rem", color: "#475569",
                        lineHeight: 1.65, borderTop: "1px solid #dbeafe"
                      }}>
                        <p style={{ marginTop: "0.6rem" }}>{item.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer link to manual */}
          <div style={{
            padding: "1rem 1.5rem", borderTop: "1px solid #e2e8f0",
            background: "#f8fafc", textAlign: "center"
          }}>
            <p style={{ fontSize: "0.82rem", color: "#64748b", margin: 0 }}>
              Can't find what you need?{" "}
              <a
                href="/training-manual.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="help-footer-manual-link"
                style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none" }}
              >
                Open the complete Training Manual
              </a>
              {" "}or submit a ticket at the Support Portal for further assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
